import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Popconfirm, Row, Table, TableColumnsType, Typography } from "antd";
import { FilterValue, SorterResult, TablePaginationConfig } from "antd/lib/table/interface";
import axios from "axios";
import { message } from "components/Messages/Messages";
import MobileTable from "components/MobileTable/MobileTable";
import SearchBar from "components/SearchBar/SearchBar";
import useViewport from "components/UseViewport/UseViewport";
import { StudyProgram } from "interfaces/students";
import { StudentUser } from "interfaces/users";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface StudentTableFilters {
  year_level?: number[];
  study_programme?: number[];
  has_graduated?: string[];
}

const AdminStudentsList = () => {
  const { Title } = Typography;
  const history = useHistory();
  const { width } = useViewport();
  const [studyPrograms, setStudyPrograms] = useState<StudyProgram[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalStudents, setTotalStudents] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filters, setFilters] = useState<StudentTableFilters>({});
  const [ordering, setOrdering] = useState<string>("");

  useEffect(() => {
    axios
      .get<StudyProgram[]>("/students/study-program/")
      .then(response => {
        setStudyPrograms(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const [students, setStudents] = useState<StudentUser[]>([]);

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();

    // Pagination
    params.append("page", currentPage.toString());
    params.append("page_size", pageSize.toString());

    // Search
    if (searchTerm) {
      params.append("search", searchTerm);
    }

    // Filtering
    if (filters.year_level) {
      filters.year_level.forEach((level: number) => {
        params.append("year_level", level.toString());
      });
    }

    if (filters.study_programme) {
      filters.study_programme.forEach((programId: number) => {
        params.append("study_programme", programId.toString());
      });
    }

    if (filters.has_graduated) {
      filters.has_graduated.forEach((status: string) => {
        params.append("has_graduated", status);
      });
    }

    // Sorting
    if (ordering) {
      params.append("ordering", ordering);
    }

    return params.toString();
  }, [currentPage, pageSize, searchTerm, filters, ordering]);

  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const queryString = buildQueryString();
        const response = await axios.get<PaginatedResponse<StudentUser>>(`/students/?${queryString}`);
        setStudents(response.data.results);
        setTotalStudents(response.data.count);
      } catch (error) {
        message("error", "Kunne ikke hente studenter. Prøv igjen senere.");
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [buildQueryString]);

  const handleSearch = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    tableFilters: Record<string, FilterValue | null>,
    sorter: SorterResult<StudentUser> | SorterResult<StudentUser>[]
  ) => {
    const newFilters: Record<string, string[]> = {};
    Object.entries(tableFilters).forEach(([key, value]) => {
      if (value) {
        newFilters[key] = value as string[];
      }
    });

    // Handle sorting
    let newOrdering = "";
    if (sorter && !Array.isArray(sorter) && sorter.column) {
      const direction = sorter.order === "ascend" ? "" : "-";
      const field = sorter.field === "name" ? "name" : sorter.field;
      newOrdering = `${direction}${field}`;
    }

    setFilters(newFilters);
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
    if (pagination.pageSize) {
      setPageSize(pagination.pageSize);
    }
    setOrdering(newOrdering);
  };

  const onRowClick = (studentID: number) => ({
    onClick: () => {
      history.push(`/admin/students/${studentID}`);
    },
    style: {
      cursor: "pointer",
    },
  });

  const columns: TableColumnsType<StudentUser> = [
    {
      title: "Brukernavn",
      dataIndex: "username",
      key: "username",
      ellipsis: true,
      width: 150,
      sorter: true,
    },
    {
      title: "Navn",
      dataIndex: "name",
      key: "name",
      render: (text, record) => `${record.first_name} ${record.last_name}`,
      sorter: true,
      ellipsis: true,
    },
    {
      title: "Mail",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
      width: 200,
    },
    {
      title: "Linje",
      render: (text, record) => studyPrograms.find(program => program.id === record.study_programme)?.name ?? "Ukjent",
      key: "study_programme",
      filters: studyPrograms.map(program => ({ text: program.name, value: program.id })),
      filteredValue: filters.study_programme || null,
      ellipsis: true,
      width: 320,
    },
    {
      title: "Årstrinn",
      dataIndex: "year_level",
      key: "year_level",
      filters: [
        { text: "1", value: 1 },
        { text: "2", value: 2 },
        { text: "3", value: 3 },
        { text: "4", value: 4 },
        { text: "5", value: 5 },
      ],
      filteredValue: filters.year_level,
      width: 100,
    },
    {
      title: "Sluttet",
      key: "has_graduated",
      dataIndex: "has_graduated",
      render: (text, record) => (!record.has_graduated ? "Nei" : "Ja"),
      filters: [
        { text: "Ja", value: "True" },
        { text: "Nei", value: "False" },
      ],
      filteredValue: filters.has_graduated || null,
      ellipsis: true,
      width: 80,
    },
    {
      key: "edit",
      render: () => <EditOutlined style={{ fontSize: 20, cursor: "pointer" }} />,
      width: 50,
    },
    {
      key: "x",
      render: (text, record) => {
        return (
          <Popconfirm
            key="delete-article"
            title={`Slett "${record.first_name} ${record.last_name}"`}
            okText="Slett"
            placement="top"
            cancelText="Avbryt"
            style={{ backgroundColor: "var(--neutral-alt)" }}
            okType="danger"
            onConfirm={() => {
              axios
                .delete<StudentUser>(`/students/${record.id}/`)
                .then(() => {
                  message("success", "Student slettet");
                  setStudents(students.filter(student => student.id !== record.id));
                })
                .catch(() => message("error", "Kunne ikke slette student. Prøv igjen senere."));
            }}
          >
            <DeleteOutlined style={{ color: "red", fontSize: 20, cursor: "pointer" }} />
          </Popconfirm>
        );
      },
      width: 50,
    },
  ];

  return (
    <>
      <Card>
        <Row justify="space-between" align="middle">
          <Title level={2}>Studenter</Title>
          <Button type="primary" icon={<PlusOutlined />}>
            Opprett ny student
          </Button>
        </Row>
        <Row style={{ margin: "25px auto", width: "100%" }}>
          <SearchBar value={searchTerm} placeholder="Søk etter navn eller brukernavn..." handleSearch={handleSearch} />
        </Row>
        {width > 768 ? (
          <Table
            columns={columns}
            dataSource={students}
            onChange={handleTableChange}
            onRow={event => onRowClick(event.id)}
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalStudents,
              showSizeChanger: true,
              showTotal: total => `${total} studenter matcher kriteriene`,
            }}
          />
        ) : (
          <MobileTable
            columns={[
              {
                title: "Brukernavn",
                dataIndex: "username",
                key: "username",
              },
              {
                title: "Navn",
                dataIndex: "name",
                key: "name",
                render: (value, record) => `${record.first_name} ${record.last_name}`,
              },
              {
                title: "Mail",
                dataIndex: "email",
                key: "email",
              },
              {
                title: "Linje",
                dataIndex: "study_programme",
                key: "study_programme",
                render: value => studyPrograms.find(program => program.id === value)?.name ?? "Ukjent",
              },
              {
                title: "Årstrinn",
                dataIndex: "year_level",
                key: "year_level",
              },
              {
                title: "Sluttet",
                dataIndex: "has_graduated",
                key: "has_graduated",
                render: value => (value ? "Ja" : "Nei"),
              },
            ]}
            data={students}
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalStudents,
              onChange: (page, pSize) => {
                setCurrentPage(page);
                setPageSize(pSize);
              },
            }}
            onClick={id => history.push(`/admin/students/${id}`)}
            emptyText="Ingen studenter funnet"
          />
        )}
      </Card>
    </>
  );
};

export default AdminStudentsList;
